import "./Thumbs.scss";

import React, { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useDispatch } from "react-redux";

import { Text } from "../text";
import { TappableSpan } from "../../common/tappable";
import { ThumbIcon } from "../../assets/icons";

import { sendSurvey } from "../../../features/survey/actions/sendSurvey";
import { GoogleReviewsSettings } from "../../../features/survey/reducers/types";

import { GoogleReviewSection } from "./GoogleReviewSection";
import { actions } from "src/features/survey/actions";

export enum Rating {
    NEGATIVE = 0,
    POSITIVE,
}

export interface ThumbsProps {
    onThumbClick: (rating: Rating) => void;
    title?: string;
    rating?: Rating;
    defaultRating?: Rating | null;
    isLargeTitle?: boolean;
    isLargeIcons?: boolean;
    locationTheme?: boolean;
    showSelectedOnly?: boolean;
    googleReviewsSettings?: GoogleReviewsSettings;
}

export const Thumbs = ({
    onThumbClick,
    title,
    rating,
    defaultRating,
    isLargeTitle,
    isLargeIcons,
    locationTheme,
    showSelectedOnly,
    googleReviewsSettings,
}: ThumbsProps) => {
    const dispatch = useDispatch();
    const googleReviewTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [selectedRating, setSelectedRating] = useState<undefined | Rating | null>(defaultRating);

    const googleReviewsEnabled = googleReviewsSettings?.enabled ?? false;
    const googleBusinessPlaceId = googleReviewsSettings?.businessPlaceId ?? "";
    const googleReviewsAutoRedirectEnabled = googleReviewsSettings?.enableAutoRedirect ?? false;
    const googleReviewHref = googleBusinessPlaceId
        ? `https://search.google.com/local/writereview?placeid=${googleBusinessPlaceId ?? ""}`
        : "https://developers.google.com/maps/documentation/places/web-service/place-id";

    const onIconClick = useCallback(
        (rating: Rating) => {
            dispatch(actions.setRating(rating));

            if (selectedRating !== rating) {
                setSelectedRating(rating);
                onThumbClick(rating);
            }
        },
        [onThumbClick, selectedRating, dispatch]
    );

    useEffect(() => {
        if (rating !== undefined && rating !== selectedRating) setSelectedRating(rating);
    }, [rating, selectedRating]);

    return (
        <div
            className={classNames(
                "thumbs",
                locationTheme && "thumbs-location-theme",
                showSelectedOnly && selectedRating !== null && selectedRating !== undefined && "selected"
            )}
        >
            {title && (
                <Text
                    preset={isLargeTitle ? "title-28" : "g-16"}
                    mode="bold"
                    value={title}
                    className={classNames("thumbs__title", isLargeTitle && "large")}
                />
            )}
            <div className="thumbs__icons">
                <TappableSpan
                    className={classNames(
                        "thumbs__icons__down",
                        selectedRating === Rating.NEGATIVE && "active",
                        isLargeIcons && "large"
                    )}
                    onClick={() => onIconClick(Rating.NEGATIVE)}
                >
                    <ThumbIcon />
                </TappableSpan>
                <TappableSpan
                    className={classNames(
                        "thumbs__icons__up",
                        selectedRating === Rating.POSITIVE && "active",
                        isLargeIcons && "large"
                    )}
                    onClick={() => {
                        onIconClick(Rating.POSITIVE);
                        if (googleReviewsEnabled && googleReviewsAutoRedirectEnabled) {
                            dispatch(sendSurvey(true));
                            window.open(googleReviewHref, "_blank");
                            googleReviewTimeoutRef.current = null;
                        } else if (googleReviewsEnabled && !googleReviewsAutoRedirectEnabled) {
                            dispatch(sendSurvey(true));
                        }
                    }}
                >
                    <ThumbIcon />
                </TappableSpan>
            </div>
            {googleReviewsEnabled && !googleReviewsAutoRedirectEnabled && (
                <div className={`google-review-transition ${selectedRating === Rating.POSITIVE ? "show" : "hide"}`}>
                    <GoogleReviewSection isLargeTitle={isLargeTitle} businessPlaceId={googleBusinessPlaceId} />
                </div>
            )}
        </div>
    );
};
